import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import PpobService from 'service/PPOB/PpobService';
import { ProductItem } from 'domain/payload/response/PPOB/PpobProductItemResp';

interface PpobProductItemDetailModalProps {
  isOpen: boolean;
  itemId: string | null;
  onClose: () => void;
  onOpenEditPricing?: (item: ProductItem) => void;
  canUpdate?: boolean;
}

const PpobProductItemDetailModal: React.FC<PpobProductItemDetailModalProps> = ({
  isOpen,
  itemId,
  onClose,
  onOpenEditPricing,
  canUpdate = false,
}) => {
  const { profile } = useSelector((state: any) => state.auth);
  const ppobService = PpobService();

  const [detail, setDetail] = useState<ProductItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const fetchDetail = async () => {
    if (!itemId) return;
    const token = profile?.data?.token;
    if (!token) return;

    setLoading(true);
    setErrorMsg(null);
    try {
      const res = await ppobService.getProductItemDetail(token, itemId);
      setDetail(res.data || null);
    } catch (err: any) {
      console.error('Failed to fetch product item detail:', err);
      const msg = typeof err === 'string' ? err : err.message || 'Failed to fetch product item detail';
      setErrorMsg(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && itemId) {
      fetchDetail();
    } else {
      setDetail(null);
      setErrorMsg(null);
    }
  }, [isOpen, itemId]);

  if (!isOpen) return null;

  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      maximumFractionDigits: 0,
    }).format(val || 0);
  };

  const transmoraMargin = detail?.pricing?.transmoraMargin ?? detail?.transmoraMargin ?? 0;
  const partnerMargin = detail?.pricing?.defaultPartnerMargin ?? detail?.defaultPartnerMargin ?? 0;
  const discount = detail?.pricing?.discount ?? detail?.discount ?? 0;
  const basePrice = detail?.price ?? 0;
  const estimatedSellingPrice = Math.max(0, basePrice + transmoraMargin + partnerMargin - discount);

  return (
    <div
      className="modal fade show"
      tabIndex={-1}
      style={{
        display: 'block',
        backgroundColor: 'rgba(0,0,0,0.5)',
        overflowY: 'auto',
      }}
      onClick={onClose}
    >
      <div className="modal-dialog modal-dialog-centered modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">PPOB Item Detail</h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>

          <div className="modal-body">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : errorMsg ? (
              <div className="alert alert-danger">{errorMsg}</div>
            ) : detail ? (
              <div>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-semibold">Item ID</label>
                    <input
                      type="text"
                      className="form-control form-control-compact"
                      value={detail.id}
                      disabled
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-semibold">Item Status</label>
                    <div>
                      {detail.isActive ? (
                        <span className="badge bg-success">Active</span>
                      ) : (
                        <span className="badge bg-danger">Inactive</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-semibold">Code</label>
                    <input
                      type="text"
                      className="form-control form-control-compact"
                      value={detail.code}
                      disabled
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-semibold">Name</label>
                    <input
                      type="text"
                      className="form-control form-control-compact"
                      value={detail.name}
                      disabled
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Description</label>
                  <input
                    type="text"
                    className="form-control form-control-compact"
                    value={detail.description || '-'}
                    disabled
                  />
                </div>

                <div className="row">
                  <div className="col-md-3 mb-3">
                    <label className="form-label fw-semibold">Group</label>
                    <input
                      type="text"
                      className="form-control form-control-compact"
                      value={detail.groupName || '-'}
                      disabled
                    />
                  </div>
                  <div className="col-md-3 mb-3">
                    <label className="form-label fw-semibold">Product</label>
                    <input
                      type="text"
                      className="form-control form-control-compact"
                      value={detail.productName || '-'}
                      disabled
                    />
                  </div>
                  <div className="col-md-3 mb-3">
                    <label className="form-label fw-semibold">Product Status</label>
                    <div title="You can change this product status in PPOB Configuration page">
                      {detail.productIsActive ? (
                        <span className="badge bg-success">Active</span>
                      ) : (
                        <span className="badge bg-danger">Inactive</span>
                      )}
                    </div>
                  </div>
                  <div className="col-md-3 mb-3">
                    <label className="form-label fw-semibold">Biller</label>
                    <input
                      type="text"
                      className="form-control form-control-compact"
                      value={detail.billerName || detail.billerCode || '-'}
                      disabled
                    />
                  </div>
                </div>

                <hr />

                <div className="row">
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-semibold">Base Price</label>
                    <input
                      type="text"
                      className="form-control form-control-compact"
                      value={formatCurrency(basePrice)}
                      disabled
                    />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-semibold">Admin Fee</label>
                    <input
                      type="text"
                      className="form-control form-control-compact"
                      value={formatCurrency(detail.adminFee || 0)}
                      disabled
                    />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-semibold">Transmora Margin</label>
                    <input
                      type="text"
                      className="form-control form-control-compact"
                      value={formatCurrency(transmoraMargin)}
                      disabled
                    />
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-semibold">Default Partner Margin</label>
                    <input
                      type="text"
                      className="form-control form-control-compact"
                      value={formatCurrency(partnerMargin)}
                      disabled
                    />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-semibold">Discount</label>
                    <input
                      type="text"
                      className="form-control form-control-compact"
                      value={formatCurrency(discount)}
                      disabled
                    />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-semibold">Estimated Selling Price</label>
                    <input
                      type="text"
                      className="form-control form-control-compact fw-bold text-primary"
                      value={formatCurrency(estimatedSellingPrice)}
                      disabled
                    />
                  </div>
                </div>

                <hr />

                <div className="row">
                  <div className="col-md-6 mb-2">
                    <small className="text-muted d-block">Created At</small>
                    <span>{detail.createdAt || '-'}</span>
                  </div>
                  <div className="col-md-6 mb-2">
                    <small className="text-muted d-block">Updated At</small>
                    <span>{detail.updatedAt || '-'}</span>
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
            {canUpdate && detail && onOpenEditPricing && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  onClose();
                  onOpenEditPricing(detail);
                }}
              >
                Edit Pricing
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PpobProductItemDetailModal;
